import Flexbox from "$/lib/components/common/Flexbox";
import type { ThemeChoices } from "$/lib/types/General.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import type { TooltipProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  hideKeys,
  displayNames,
  displayUnits,
  theme,
  reverseTooltipOrder = false,
}: TooltipProps<ValueType, NameType> & {
  unit: string;
  hideKeys?: string[];
  theme?: ThemeChoices;
  displayNames?: Record<string, string>;
  displayUnits?: Record<string, string>;
  reverseTooltipOrder?: boolean;
}) => {
  if (active && payload && payload.length) {
    const getValue = (value?: ValueType, name?: NameType): string => {
      const displayUnit =
        displayUnits && name ? (displayUnits[name] ?? unit) : unit;

      if (value === undefined || value === null) return "-";
      if (typeof value === "number")
        return numberFormatter(value) + displayUnit;
      if (Array.isArray(value))
        return "[" + value.map((item) => item).join(" - ") + "]";
      return String(value) + displayUnit;
    };

    return (
      <Flexbox
        fullWidth
        align="stretch"
        className="w-full min-w-40 rounded bg-white shadow-md"
      >
        <p
          className={cn(
            "flex h-7 grow items-center rounded-t bg-primary px-3 text-sm font-normal text-white",
            {
              "bg-primary": theme === "blue",
              "bg-forest": theme === "green",
              "bg-warning": theme === "orange",
            },
          )}
        >
          {label} {/* Date */}
        </p>
        {(reverseTooltipOrder ? [...payload].reverse() : payload).map(
          (entry, index) => {
            if (
              hideKeys &&
              entry.name &&
              hideKeys.find((key) => key === entry.name)
            )
              return null;

            return (
              <div
                key={"tooltip-" + index}
                className="flex h-8 grow items-center gap-1 px-3 text-xs font-light text-tertiary-700"
              >
                <div
                  className="size-2 rounded-full"
                  style={{
                    backgroundColor:
                      !entry.stroke || entry.stroke === "none"
                        ? // @ts-expect-error - typescript error from the side of the library
                          entry.fill
                        : entry.stroke,
                  }}
                />
                <span>
                  {displayNames && entry.name
                    ? (displayNames[entry.name] ?? entry.name)
                    : entry.name}
                  {" : "}
                  {getValue(entry.value, entry.name)}
                </span>
              </div>
            );
          },
        )}
      </Flexbox>
    );
  }
  return null;
};

export default CustomTooltip;
